<script setup>
import { Link } from '@inertiajs/inertia-vue3';
import { computed } from 'vue';
import { Icon } from '@iconify/vue';

const props = defineProps({
    href: {
        type: String,
        required: false,
        default: null
    },
    label: {
        type: String,
        default: ''
    },
    icon: {
        type: String,
        default: ''
    },
    size: {
        type: String,
        default: 'md',
    },
    color: {
        type: String, // Es buena práctica definir el tipo
        default: 'primary'
    },
    hideLabelOnMobile: {
        type: Boolean,
        default: false
    }
});

// 1. Añadimos justify-center para que el Link (<a>) centre el texto igual que un botón
const baseClasses = 'inline-flex items-center justify-center gap-2 border transition ease-in-out duration-150 focus:outline-none focus:ring-4';

const sizeClasses = {
    md: 'rounded-md px-4 py-2 text-xs h-[38px] uppercase tracking-widest',
    sm: 'rounded-md px-2 py-1 text-sm h-[28px]',
    xs: 'rounded-md px-1 py-0.5 text-xs',
    // Usamos flex para el icono
    icon: 'rounded-md text-xs w-[38px] h-[38px] flex justify-center items-center',
    iconSm: 'rounded-md text-xs w-[28px] h-[28px] flex justify-center items-center',
    roundedSm: 'rounded-full w-[28px] h-[28px]',
    rounded: 'rounded-full w-[38px] h-[38px]',

    vuexysm: 'rounded-lg px-2 py-0.5 text-xs h-[28px] tracking-widest font-normal',
    vuexy: 'rounded-xl px-2 py-1 text-sm h-[38px] tracking-widest font-medium',
    vuexylg: 'rounded-xl px-6 py-3 text-base h-[48px] tracking-widest font-semibold',
    vuexyxl: 'rounded-2xl px-8 py-4 text-base h-[58px] tracking-widest font-semibold'
};

const mobileIconOnlyClasses = {
    md: 'w-[38px] !px-0 sm:!w-auto sm:!px-4',
    sm: 'w-[28px] !px-0 sm:!w-auto sm:!px-2',
    xs: 'w-[24px] !px-0 sm:!w-auto sm:!px-1',
    icon: '',
    iconSm: '',
    roundedSm: '',
    rounded: '',
    vuexysm: 'w-[28px] !px-0 sm:!w-auto sm:!px-2',
    vuexy: 'w-[38px] !px-0 sm:!w-auto sm:!px-2',
    vuexylg: 'w-[48px] !px-0 sm:!w-auto sm:!px-6',
    vuexyxl: 'w-[58px] !px-0 sm:!w-auto sm:!px-8'
};

const colorClasses = {
    primary: 'text-white border-transparent focus:ring-gray-700/20 bg-gray-800 hover:bg-gray-700 active:bg-gray-900',
    gray: 'text-gray-700 border-gray-200 focus:ring-gray-500/20 bg-gray-200 hover:bg-gray-300 active:bg-gray-400',
    light: 'text-gray-700 border-gray-300 focus:ring-gray-200/50 bg-white hover:bg-gray-50 active:bg-gray-100',
    red: 'text-white border-transparent focus:ring-red-500/50 bg-red-600 hover:bg-red-500 active:bg-red-700',
    blue: 'text-white border-transparent focus:ring-blue-500/50 bg-blue-600 hover:bg-blue-500 active:bg-blue-700',
    green: 'text-white border-transparent focus:ring-green-500/50 bg-green-500 hover:bg-green-600 active:bg-green-700',
    yellow: 'text-white border-transparent focus:ring-yellow-500/50 bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700',
    borderLight: 'text-white hover:text-gray-700 border-white focus:ring-gray-200/50 bg-none hover:bg-gray-100',
}

const classes = computed(() => [
    baseClasses,
    sizeClasses[props.size] || sizeClasses.md,
    props.hideLabelOnMobile ? (mobileIconOnlyClasses[props.size] || mobileIconOnlyClasses.md) : '',
    colorClasses[props.color] || colorClasses.primary
]);

const componentType = computed(() => props.href ? Link : 'button');
</script>

<template>
    <component 
        :is="componentType" 
        :href="href" 
        :class="classes"
        v-bind="$attrs"
    >
        <Icon v-if="icon" :icon="icon" class="w-4 h-4 shrink-0" />
        <span v-if="label" :class="{ 'hidden sm:inline': hideLabelOnMobile }">{{ label }}</span>
        <slot v-else />
    </component>
</template>

<script>
// Esto es para que los atributos no se dupliquen si tienes un div envoltorio
export default { inheritAttrs: false }
</script>

